import React, { useEffect } from 'react';
import { AuthPage } from '../auth-page-wrapper';
import { useNavigate } from 'react-router-dom';
import { Lang, OTP_ERROR_MESSAGES, ROUTES } from 'shared/constants';
import styles from './verification.module.scss';
import _ from 'lodash';
import { Loader, OtpInput } from '../../../components/core';
import UserService from 'shared/services/user.service';
import { Button } from 'src/components/design-system';
import { NavLink } from 'react-router-dom';

const MAX_TIMER_SECONDS = 90;
const SECONDS_IN_MINUTE = 60;
const AMOUNT_OF_ATTEMPTS = 3;
const MAX_RESEND_MINUTES = 10;
const ONE_SECOND = 1000;

const displayTimeEllapsed = seconds => {
  const minutes = Math.floor(seconds / SECONDS_IN_MINUTE);
  const remainingSeconds = seconds % SECONDS_IN_MINUTE;
  return `0${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
};

const VerificationPageComponent = React.memo(({ lang = Lang.OTP_SCREEN, email = '' }) => {
  const navigate = useNavigate();

  const [error, setError] = React.useState(null);
  const [loading, setLoadingState] = React.useState(false);
  const [attemptsExceed, setAttemptsExceed] = React.useState(false);
  const [secondsToResend, setSecondsToResend] = React.useState(MAX_TIMER_SECONDS);
  const [timerIsActive, setTimerIsActive] = React.useState(true);
  const [nOfResends, setNOfResends] = React.useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setSecondsToResend(prev => {
        if (prev - 1 <= 0 || attemptsExceed) {
          setTimerIsActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, ONE_SECOND);

    return () => clearInterval(intervalId);
  }, [attemptsExceed]);

  useEffect(() => {
    let totalSeconds = MAX_RESEND_MINUTES * SECONDS_IN_MINUTE;
    const timerId = setInterval(() => {
      if (totalSeconds <= 0) {
        totalSeconds = MAX_RESEND_MINUTES * SECONDS_IN_MINUTE;
        setNOfResends(0);
        return 0;
      }
      totalSeconds -= 1;
    }, ONE_SECOND);

    return () => clearInterval(timerId);
  }, []);

  const verifyCode = async ({ code }) => {
    setError('');
    setAttemptsExceed(false);
    setLoadingState(true);

    let { error } = await UserService.verifyCode({ code });

    if (error) {
      if (error === OTP_ERROR_MESSAGES.CODE_DOES_NOT_MATCH) {
        setError(lang.CODE_DOES_NOT_MATCH);
      }

      if (error === OTP_ERROR_MESSAGES.VERIFICATION_ATTEMPTS_LIMIT_EXCEED) {
        setError(lang.VERIFICATION_ATTEMPTS_LIMIT_EXCEED);
        setAttemptsExceed(true);
      }
    }

    setLoadingState(false);
  };

  const resendCode = async () => {
    setError('');
    setAttemptsExceed(false);
    setLoadingState(true);

    let res = await UserService.signin({ email });

    if (res?.error) {
      setError(res.error);
    } else {
      setSecondsToResend(MAX_TIMER_SECONDS);
      setTimerIsActive(true);
      setNOfResends(prev => prev + 1);
    }
    if (nOfResends >= AMOUNT_OF_ATTEMPTS - 1) {
      setError(lang.OTP_RESEND_ATTEMPT_LIMIT_EXCEED);
    }
    setLoadingState(false);
  };

  React.useEffect(() => {
    if (_.isUndefined(email) || email === '') {
      navigate('/', { replace: true });
    }
  }, [email, navigate]);

  const content = lang.ENTER_DIGITS.replace('{email}', email);
  const disabled = (!attemptsExceed && timerIsActive) || nOfResends >= AMOUNT_OF_ATTEMPTS;

  return (
    <AuthPage title={lang.HEADER} content={content} pandaCloseEyes>
      {loading ? <Loader /> : <OtpInput onChange={verifyCode} />}
      <span className={styles.error}>{error}</span>
      {nOfResends < AMOUNT_OF_ATTEMPTS ? (
        <>
          <Button
            data-testid="signin-otp-resend"
            className={`${styles.button} ${styles.link}`}
            style={styles.button}
            onClick={resendCode}
            disabled={disabled}
            loading={loading}
          >
            <span style={{ color: 'var(--color-neutral-4)' }}>
              {lang.RESEND_VERIFICATION_CODE}
              {disabled ? ` (${displayTimeEllapsed(secondsToResend)})` : ''}
            </span>
          </Button>
          <span
            className={`${styles.label} text-label`}
            style={{
              color: 'var(--color-neutral-4)',
              marginLeft: 16,
            }}
          ></span>
        </>
      ) : (
        ''
      )}
      <p className={styles.contact}>
        <NavLink to={ROUTES.NOT_ALLOWED} className={styles.link}>
          {lang.CONTACT_US_LINK}
        </NavLink>{' '}
        {lang.CONTACT_US}
      </p>
    </AuthPage>
  );
});

export { VerificationPageComponent };
